/**
 * Coleta de sinais de rastreio do lead:
 * - Atribuição: referrer, landing_page, click IDs (gclid/fbclid/msclkid/ttclid)
 * - Identidade: visitor_id (localStorage), session_id (sessionStorage),
 *   is_returning_visitor, visit_count
 * - Dispositivo: device_type, os, browser, viewport_size
 * - Localização: timezone
 * - Comportamento: time_on_site_seconds, scroll_depth_percent,
 *   form_fill_time_seconds (por form_id)
 *
 * Tudo client-side, sem dependências externas. Persistência só em
 * local/sessionStorage do próprio navegador.
 */

const VISITOR_KEY = "kentro_visitor_v1";
const VISIT_COUNT_KEY = "kentro_visit_count_v1";
const SESSION_KEY = "kentro_session_v1";
const LANDING_KEY = "kentro_landing_v1";
const SESSION_START_KEY = "kentro_session_start_v1";
const SCROLL_KEY = "kentro_scroll_max_v1";
const CLICK_IDS_KEY = "kentro_click_ids_v1";
const UTM_KEY = "kentro_utm_v1";
const FORM_FOCUS_PREFIX = "kentro_form_focus_";
const ORIGIN_PAGE_KEY = "kentro_origin_page_v1";
const PREV_PAGE_KEY = "kentro_prev_page_v1";
const LAST_PAGE_KEY = "kentro_last_page_v1";

const CLICK_ID_KEYS = [
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
  "wbraid",
  "gbraid",
  // ChatGPT Ads (OpenAI): oppref é adicionado automaticamente para atribuição.
  "oppref",
  "ad_account_id",
] as const;
const UTM_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
] as const;

/**
 * Ignora macros de anúncio não substituídas pela plataforma
 * (ex.: `{campaign_id}`, `%7Bad_id%7D`, `[ad_id]`) — evitam sujar a atribuição.
 */
export function cleanParamValue(value: string | null | undefined): string {
  if (!value) return "";
  const v = value.trim();
  if (!v) return "";
  if (/^[{[(].*[}\])]$/.test(v)) return "";
  if (/^%7[bB]/.test(v)) return "";
  return v;
}

function uuid(): string {

  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function detectDeviceType(ua: string): string {
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod|Opera Mini|IEMobile|BlackBerry/i.test(ua)) return "mobile";
  return "desktop";
}
function detectOS(ua: string): string {
  if (/Windows NT/i.test(ua)) return "Windows";
  if (/Mac OS X/i.test(ua) && !/Mobile/i.test(ua)) return "macOS";
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Linux/i.test(ua)) return "Linux";
  return "";
}
function detectBrowser(ua: string): string {
  if (/Edg\//i.test(ua)) return "Edge";
  if (/OPR\//i.test(ua)) return "Opera";
  if (/Chrome\//i.test(ua) && !/Edg|OPR/i.test(ua)) return "Chrome";
  if (/Firefox\//i.test(ua)) return "Firefox";
  if (/Safari\//i.test(ua) && !/Chrome|Edg|OPR/i.test(ua)) return "Safari";
  return "";
}

/**
 * Inicializa rastreio assim que possível. Idempotente: pode ser chamada
 * múltiplas vezes (no mount do root) sem efeitos colaterais.
 */
export function initTracking() {
  if (typeof window === "undefined") return;
  const local = safeLocal();
  const session = safeSession();

  // visitor_id (persistente)
  if (local && !local.getItem(VISITOR_KEY)) {
    local.setItem(VISITOR_KEY, uuid());
  }

  // session_id e contadores de sessão
  if (session && !session.getItem(SESSION_KEY)) {
    session.setItem(SESSION_KEY, uuid());
    session.setItem(SESSION_START_KEY, String(Date.now()));
    // incrementa visit_count uma vez por sessão
    if (local) {
      const prev = parseInt(local.getItem(VISIT_COUNT_KEY) || "0", 10) || 0;
      local.setItem(VISIT_COUNT_KEY, String(prev + 1));
    }
  }

  // landing page (1º hit da sessão)
  if (session && !session.getItem(LANDING_KEY)) {
    session.setItem(
      LANDING_KEY,
      JSON.stringify({
        url: window.location.href,
        timestamp: new Date().toISOString(),
        referrer: document.referrer || "",
      }),
    );
  }

  // click IDs — captura na URL e persiste por sessão
  if (session) {
    const params = new URLSearchParams(window.location.search);
    const stored: Record<string, string> = session.getItem(CLICK_IDS_KEY)
      ? JSON.parse(session.getItem(CLICK_IDS_KEY)!)
      : {};
    let changed = false;
    for (const k of CLICK_ID_KEYS) {
      const v = cleanParamValue(params.get(k));
      if (v && !stored[k]) {
        stored[k] = v;
        changed = true;
      }
    }
    if (changed) session.setItem(CLICK_IDS_KEY, JSON.stringify(stored));
  }

  // UTMs — last-touch: se a URL atual trouxer qualquer utm_*, sobrescreve o set.
  if (session) {
    try {
      const params = new URLSearchParams(window.location.search);
      const incoming: Record<string, string> = {};
      for (const k of UTM_PARAM_KEYS) {
        const v = cleanParamValue(params.get(k));
        if (v) incoming[k] = v;
      }
      if (Object.keys(incoming).length > 0) {
        session.setItem(UTM_KEY, JSON.stringify(incoming));
      }
    } catch {
      /* ignore */
    }
  }


  // scroll depth máximo — sem forced reflow: cacheamos scrollHeight via
  // ResizeObserver e lemos apenas window.scrollY no handler (sem layout read).
  if (session && !(window as unknown as { __kentroScroll?: boolean }).__kentroScroll) {
    (window as unknown as { __kentroScroll?: boolean }).__kentroScroll = true;
    let ticking = false;
    let cachedMax = 1;
    let cachedViewport = window.innerHeight;
    const recompute = () => {
      // Lido fora do caminho de scroll, em rAF, para não bloquear o input.
      requestAnimationFrame(() => {
        cachedViewport = window.innerHeight;
        cachedMax = Math.max(1, document.documentElement.scrollHeight - cachedViewport);
      });
    };
    const measure = () => {
      ticking = false;
      const pct = Math.min(100, Math.round((window.scrollY / cachedMax) * 100));
      const prev = parseInt(session.getItem(SCROLL_KEY) || "0", 10) || 0;
      if (pct > prev) session.setItem(SCROLL_KEY, String(pct));
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recompute, { passive: true });
    if (typeof ResizeObserver !== "undefined") {
      try {
        const ro = new ResizeObserver(recompute);
        ro.observe(document.documentElement);
      } catch { /* ignore */ }
    }
    // Primeira medida após o layout estabilizar.
    recompute();
  }
}

export type AttributionParams = Partial<
  Record<(typeof UTM_PARAM_KEYS)[number] | (typeof CLICK_ID_KEYS)[number], string>
>;

/**
 * Lê UTMs e click IDs (gclid, fbclid, msclkid, ttclid, wbraid, gbraid) da URL
 * atual com fallback para o que foi persistido na sessão. Retorna apenas
 * chaves preenchidas — pronto pra anexar em eventos de GA4/GTM/Meta.
 */
export function readAttributionParams(): AttributionParams {
  if (typeof window === "undefined") return {};
  const out: AttributionParams = {};
  try {
    const session = safeSession();
    const params = new URLSearchParams(window.location.search);

    const readStored = (key: string): Record<string, string> => {
      try {
        const raw = session?.getItem(key);
        return raw ? (JSON.parse(raw) as Record<string, string>) || {} : {};
      } catch {
        return {};
      }
    };
    const storedUtm = readStored(UTM_KEY);
    const storedClick = readStored(CLICK_IDS_KEY);

    for (const k of UTM_PARAM_KEYS) {
      const v = params.get(k) || storedUtm[k];
      if (v) out[k] = v;
    }
    for (const k of CLICK_ID_KEYS) {
      const v = params.get(k) || storedClick[k];
      if (v) out[k] = v;
    }
  } catch {
    /* ignore */
  }
  return out;
}


/** Marca o 1º foco em um form (para calcular form_fill_time). */
export function markFormFocus(formId: string) {
  if (typeof window === "undefined") return;
  const session = safeSession();
  if (!session) return;
  const key = FORM_FOCUS_PREFIX + formId;
  if (!session.getItem(key)) session.setItem(key, String(Date.now()));
}

function readFormFillSeconds(formId: string): number {
  const session = safeSession();
  if (!session) return 0;
  const t = parseInt(session.getItem(FORM_FOCUS_PREFIX + formId) || "0", 10);
  if (!t) return 0;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

function readTimeOnSiteSeconds(): number {
  const session = safeSession();
  if (!session) return 0;
  const t = parseInt(session.getItem(SESSION_START_KEY) || "0", 10);
  if (!t) return 0;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

/** Extrai o hostname de uma URL absoluta (vazio quando inválida). */
export function hostFromUrl(url: string | undefined): string {
  if (!url) return "";
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

export type TrackingSnapshot = {
  // Atribuição
  referrer: string;
  referrer_domain: string;
  landing_page: string;
  landing_timestamp: string;
  /** Host de entrada da sessão (ex.: api.kentro.com.br). */
  landing_host: string;
  /** Host da página onde o formulário foi enviado. */
  page_host: string;
  gclid: string;
  fbclid: string;
  msclkid: string;
  ttclid: string;
  // Identidade
  visitor_id: string;
  session_id: string;
  is_returning_visitor: string; // "true" | "false"
  visit_count: string;
  // Dispositivo
  device_type: string;
  os: string;
  browser: string;
  viewport_size: string;
  screen_resolution: string;
  // Localização
  timezone: string;
  // Comportamento
  time_on_site_seconds: string;
  scroll_depth_percent: string;
  form_fill_time_seconds: string;
};

const EMPTY_SNAPSHOT: TrackingSnapshot = {
  referrer: "",
  referrer_domain: "",
  landing_page: "",
  landing_timestamp: "",
  landing_host: "",
  page_host: "",
  gclid: "",
  fbclid: "",
  msclkid: "",
  ttclid: "",
  visitor_id: "",
  session_id: "",
  is_returning_visitor: "false",
  visit_count: "0",
  device_type: "",
  os: "",
  browser: "",
  viewport_size: "",
  screen_resolution: "",
  timezone: "",
  time_on_site_seconds: "0",
  scroll_depth_percent: "0",
  form_fill_time_seconds: "0",
};

export function readTrackingSnapshot(formId: string): TrackingSnapshot {
  if (typeof window === "undefined") return { ...EMPTY_SNAPSHOT };

  try {
    // garante init mesmo se o root ainda não tiver montado
    initTracking();

    const local = safeLocal();
    const session = safeSession();
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";

    // Landing
    let landingUrl = "";
    let landingTs = "";
    try {
      const raw = session?.getItem(LANDING_KEY);
      if (raw) {
        const p = JSON.parse(raw) as { url?: string; timestamp?: string };
        landingUrl = p.url || "";
        landingTs = p.timestamp || "";
      }
    } catch {
      /* ignore */
    }
    // Fallback: usa URL atual como landing page se nada foi capturado.
    if (!landingUrl) landingUrl = window.location?.href || "";
    if (!landingTs) landingTs = new Date().toISOString();

    // Referrer — fallback para "(direct)" quando ausente, útil para atribuição.
    const rawRef = (typeof document !== "undefined" && document.referrer) || "";
    const ref = rawRef || "(direct)";
    let refDomain = "";
    try {
      if (rawRef) refDomain = new URL(rawRef).hostname;
    } catch {
      /* ignore */
    }
    if (!refDomain) refDomain = rawRef ? "" : "(direct)";

    // Click IDs
    let clickIds: Record<string, string> = {};
    try {
      const raw = session?.getItem(CLICK_IDS_KEY);
      if (raw) clickIds = JSON.parse(raw) || {};
    } catch {
      /* ignore */
    }

    const visitCountRaw = parseInt(local?.getItem(VISIT_COUNT_KEY) || "0", 10) || 0;

    return {
      referrer: ref,
      referrer_domain: refDomain,
      landing_page: landingUrl,
      landing_timestamp: landingTs,
      landing_host: hostFromUrl(landingUrl) || (window.location?.hostname || ""),
      page_host: window.location?.hostname || "",
      gclid: clickIds.gclid || "",
      fbclid: clickIds.fbclid || "",
      msclkid: clickIds.msclkid || "",
      ttclid: clickIds.ttclid || "",
      visitor_id: local?.getItem(VISITOR_KEY) || "",
      session_id: session?.getItem(SESSION_KEY) || "",
      is_returning_visitor: visitCountRaw > 1 ? "true" : "false",
      visit_count: String(visitCountRaw || 1),
      device_type: detectDeviceType(ua),
      os: detectOS(ua),
      browser: detectBrowser(ua),
      viewport_size: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
      screen_resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      timezone:
        (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) || "",
      time_on_site_seconds: String(readTimeOnSiteSeconds()),
      scroll_depth_percent: session?.getItem(SCROLL_KEY) || "0",
      form_fill_time_seconds: String(readFormFillSeconds(formId)),
    };
  } catch {
    return { ...EMPTY_SNAPSHOT };
  }
}

export type ParsedPhoneBR = {
  e164: string;
  ddd: string;
  national: string;
  digits: string;
  valid: boolean;
};

/**
 * Normaliza telefone BR para E.164 e extrai DDD.
 * Aceita 10 ou 11 dígitos nacionais (com ou sem DDI 55).
 * Ex.: "(11) 99999-0000" → { e164: "+5511999990000", ddd: "11", valid: true }
 */
export function parsePhoneBR(phone: string): ParsedPhoneBR {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return { e164: "", ddd: "", national: "", digits: "", valid: false };

  let national = digits;
  // Remove DDI 55 quando presente (mas só se sobrar tamanho nacional válido).
  if (national.startsWith("55") && (national.length === 12 || national.length === 13)) {
    national = national.slice(2);
  }
  // Remove 0 inicial de chamada nacional, se presente
  if (national.length === 11 && national.startsWith("0")) national = national.slice(1);

  const valid = national.length === 10 || national.length === 11;
  const ddd = valid ? national.slice(0, 2) : "";
  const e164 = valid ? `+55${national}` : "";
  return { e164, ddd, national: valid ? national : "", digits, valid };
}

/* ------------------------------------------------------------------
 * Origem interna do lead (qual página/LP trouxe o visitante)
 * ------------------------------------------------------------------ */

/**
 * Registra a navegação SPA para sabermos a origem real do lead:
 * - origin_page: 1ª página da sessão (LP de entrada)
 * - previous_page: página anterior ao form (ex.: /ia antes de /contato)
 * Idempotente; deve ser chamada em cada mudança de rota.
 */
export function recordPageView(path: string) {
  if (typeof window === "undefined") return;
  const session = safeSession();
  if (!session) return;
  const clean = (path || "/").replace(/\/+$/, "") || "/";
  try {
    if (!session.getItem(ORIGIN_PAGE_KEY)) session.setItem(ORIGIN_PAGE_KEY, clean);
    const last = session.getItem(LAST_PAGE_KEY);
    if (last && last !== clean) session.setItem(PREV_PAGE_KEY, last);
    session.setItem(LAST_PAGE_KEY, clean);
  } catch {
    /* ignore */
  }
}

export type OriginSignals = {
  origin_page: string;
  landing_host: string;
  previous_page: string;
  landing_path: string;
  referrer_domain: string;
};

/** Lê os sinais de origem da sessão (seguro em SSR). */
export function readOriginSignals(): OriginSignals {
  const empty: OriginSignals = {
    origin_page: "",
    landing_host: "",
    previous_page: "",
    landing_path: "",
    referrer_domain: "",
  };
  if (typeof window === "undefined") return empty;
  const session = safeSession();
  let landingPath = "";
  let landingHost = "";
  try {
    const raw = session?.getItem(LANDING_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { url?: string };
      if (p.url) {
        landingPath = new URL(p.url).pathname.replace(/\/+$/, "") || "/";
        landingHost = hostFromUrl(p.url);
      }
    }
  } catch {
    /* ignore */
  }
  let refDomain = "";
  try {
    const ref = document.referrer;
    if (ref) {
      const u = new URL(ref);
      if (u.origin !== window.location.origin) refDomain = u.hostname.replace(/^www\./, "");
    }
  } catch {
    /* ignore */
  }
  return {
    landing_host: landingHost || (typeof window !== "undefined" ? window.location.hostname : ""),
    origin_page: session?.getItem(ORIGIN_PAGE_KEY) || landingPath,
    previous_page: session?.getItem(PREV_PAGE_KEY) || "",
    landing_path: landingPath,
    referrer_domain: refDomain,
  };
}

/**
 * Sinais adicionais de identidade publicitária/analítica lidos de cookies e da
 * página, enviados junto ao lead para casar com GA4 e Meta (CAPI/Pixel).
 */
export type AdIdentitySnapshot = {
  wbraid: string;
  gbraid: string;
  oppref: string;
  ad_account_id: string;
  fbp: string;
  fbc: string;
  ga_client_id: string;
  ga_session_id: string;
  page_title: string;
  consent_analytics: string;
  consent_marketing: string;
};

const EMPTY_AD_IDENTITY: AdIdentitySnapshot = {
  wbraid: "",
  gbraid: "",
  oppref: "",
  ad_account_id: "",
  fbp: "",
  fbc: "",
  ga_client_id: "",
  ga_session_id: "",
  page_title: "",
  consent_analytics: "false",
  consent_marketing: "false",
};

function readCookie(name: string): string {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
  } catch {
    return "";
  }
}

export function readAdIdentitySnapshot(): AdIdentitySnapshot {
  if (typeof window === "undefined") return { ...EMPTY_AD_IDENTITY };
  try {
    const session = safeSession();
    let clickIds: Record<string, string> = {};
    try {
      const raw = session?.getItem(CLICK_IDS_KEY);
      if (raw) clickIds = JSON.parse(raw) || {};
    } catch {
      /* ignore */
    }
    const params = new URLSearchParams(window.location.search);

    // GA4: _ga=GA1.1.<client_id> ; _ga_<container>=GS1.1.<session_id>....
    const ga = readCookie("_ga");
    const gaClientId = ga ? ga.split(".").slice(-2).join(".") : "";
    let gaSessionId = "";
    try {
      const gaSessionCookie = document.cookie
        .split("; ")
        .find((c) => /^_ga_[A-Z0-9]+=/.test(c));
      if (gaSessionCookie) {
        // Formato: _ga_<CONTAINER>=GS2.1.s<session_id>$o1$g0$t...
        const value = gaSessionCookie.split("=")[1] || "";
        const m = value.match(/\.s(\d+)/);
        gaSessionId = m ? m[1] : "";
      }
    } catch {
      /* ignore */
    }

    // fbc: derivado do fbclid quando o cookie ainda não existe.
    const fbclid = params.get("fbclid") || clickIds.fbclid || "";
    const fbcCookie = readCookie("_fbc");
    const fbc = fbcCookie || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : "");

    let analytics = "false";
    let marketing = "false";
    try {
      const raw = window.localStorage.getItem("kentro-cookie-consent");
      if (raw) {
        const c = JSON.parse(raw) as { analytics?: boolean; marketing?: boolean };
        analytics = String(!!c.analytics);
        marketing = String(!!c.marketing);
      }
    } catch {
      /* ignore */
    }

    return {
      wbraid: params.get("wbraid") || clickIds.wbraid || "",
      gbraid: params.get("gbraid") || clickIds.gbraid || "",
      oppref: params.get("oppref") || clickIds.oppref || "",
      ad_account_id: params.get("ad_account_id") || clickIds.ad_account_id || "",
      fbp: readCookie("_fbp"),
      fbc,
      ga_client_id: gaClientId,
      ga_session_id: gaSessionId,
      page_title: (typeof document !== "undefined" && document.title) || "",
      consent_analytics: analytics,
      consent_marketing: marketing,
    };
  } catch {
    return { ...EMPTY_AD_IDENTITY };
  }
}
